import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Instagram, ArrowRight, Send } from 'lucide-react';
import { companyInfo, navLinks, services } from '../../data/mock';

const Footer = () => {
  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for subscribing!');
  };

  return (
    <footer className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f1a] text-white">
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="bg-gradient-to-r from-[#B8336A]/20 to-[#2D6A6A]/20 rounded-3xl p-8 md:p-12 backdrop-blur-sm border border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-2">Ready to Transform Your Business?</h3>
                <p className="text-gray-300">Let's discuss how we can help you achieve your digital goals.</p>
              </div>
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-[#B8336A]/30 transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
              >
                Start Your Project
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-12 w-auto bg-white rounded-lg p-1" />
              <div>
                <h3 className="text-xl font-bold">{companyInfo.name}</h3>
                <p className="text-xs text-gray-400">{companyInfo.tagline}</p>
              </div>
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed">
              We provide end-to-end IT services, software development, and SaaS solutions to help businesses automate operations, improve productivity, and scale faster.
            </p>
            <div className="flex gap-4">
              {[
                { icon: Facebook, href: '#' },
                { icon: Twitter, href: '#' },
                { icon: Linkedin, href: '#' },
                { icon: Instagram, href: '#' }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#B8336A] hover:to-[#2D6A6A] transition-all duration-300 hover:-translate-y-1"
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A]" />
            </h4>
            <ul className="space-y-3">
              {navLinks.slice(0, 6).map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white flex items-center gap-2 group transition-colors"
                  >
                    <ArrowRight size={14} className="text-[#B8336A] group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 relative inline-block">
              Our Services
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A]" />
            </h4>
            <ul className="space-y-3">
              {services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to="/services"
                    className="text-gray-300 hover:text-white flex items-center gap-2 group transition-colors"
                  >
                    <ArrowRight size={14} className="text-[#2D6A6A] group-hover:translate-x-1 transition-transform" />
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info & Newsletter */}
          <div>
            <h4 className="text-lg font-semibold mb-6 relative inline-block">
              Contact Us
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A]" />
            </h4>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#B8336A] mt-1 flex-shrink-0" />
                <span className="text-gray-300 text-sm">{companyInfo.address}</span>
              </li>
              <li>
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                  <Phone size={18} className="text-[#2D6A6A]" />
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                  <Mail size={18} className="text-[#3B82F6]" />
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
            </ul>

            <h5 className="font-medium mb-3">Newsletter</h5>
            <form onSubmit={handleSubmit} className="flex">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1 px-4 py-2 rounded-l-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8336A]"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] rounded-r-lg hover:opacity-90 transition-opacity"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
